import React, { useState, useEffect, useRef } from 'react';
import './Projects.css';
import { trackProjectView, trackSectionView } from '../utils/analytics';
import portfolio_preview from '../assets/portfolio_preview.png';
import figma_preview from '../assets/figma_preview.png';
import simulated_odds_preview from '../assets/simulated_odds_preview.png';

interface Project {
  id: number;
  title: string;
  description: string;
  fullDescription: string;
  technologies: string[];
  imageUrl: string;
  videoUrl?: string;
  projectUrl?: string;
  githubUrl?: string;
}

const Projects: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const projectsRef = useRef<HTMLDivElement>(null);

  // Sample projects data - replace with your own projects
  const projects: Project[] = [
    {
      id: 1,
      title: "SimulatedOdds",
      description: "A sports betting platform to find edges.",
      fullDescription: `
        <h3>Project: Real-time Sports Betting Odds Aggregation Platform</h3>
        <p><strong>Description:</strong> Automated web scraping system that collects live betting data from multiple sportsbooks (PrizePicks, Underdog, FanDuel) every 5 minutes, providing users with line shopping capabilities to find the best odds and calculate betting edges.</p>
        
        <h4>Tech Stack:</h4>
        <ul>
          <li><strong>Frontend:</strong> Next.js, React, TypeScript, Tailwind CSS</li>
          <li><strong>Backend:</strong> Python/FastAPI, SQLite</li>
          <li><strong>Web Scraping:</strong> Playwright, Selenium, Undetected ChromeDriver</li>
          <li><strong>Infrastructure:</strong> Digital Ocean, Nginx, SSL/TLS</li>
          <li><strong>Payments:</strong> Stripe Subscriptions, Supabase Auth</li>
          <li><strong>Features:</strong> Real-time data updates, automated scraping, rate limiting, proxy rotation</li>
        </ul>
        
        <h4>Key Achievements:</h4>
        <ul>
          <li>Built anti-detection scraping system processing 1000+ odds/day</li>
          <li>Implemented secure API with authentication and rate limiting</li>
          <li>Created responsive UI with animated data visualizations</li>
          <li>Deployed production system with 99.9% uptime</li>
        </ul>
      `,
      technologies: ["Next.js", "React", "TypeScript", "FastAPI", "Stripe", "Supabase"],
      imageUrl: simulated_odds_preview,
      videoUrl: "https://www.simulatedodds.com",
      projectUrl: "https://www.simulatedodds.com",
      githubUrl: "#"
    },
    {
      id: 2,
      title: "SeatGeek Figma Plugin",
      description: "Variable Matching Plugin for SeatGeek's design system",
      fullDescription: "A figma plugin that allows for rapid design of creatives for SeatGeek's design team by matching variables to a design system.",
      technologies: ["React", "TypeScript", "Figma"],
      imageUrl: figma_preview,
      videoUrl: "https://www.youtube.com/embed/your-video-id",
      projectUrl: "https://www.figma.com/community/plugin/1428051719508732688/variable-matcher",
      githubUrl: "#"
    },
    {
      id: 3,
      title: "Portfolio Website",
      description: "This very portfolio website built with React and TypeScript",
      fullDescription: "A modern, responsive portfolio website built with React and TypeScript. Features include smooth scrolling, project modals, contact form, and a sleek design to showcase my projects and skills.",
      technologies: ["React", "TypeScript", "Node.js"],
      imageUrl: portfolio_preview,
      videoUrl: "https://www.youtube.com/embed/your-video-id",
      projectUrl: "#",
      githubUrl: "#"
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          trackSectionView('projects');
          observer.disconnect();
        }
      },
      { threshold: 0.45 }
    );

    if (projectsRef.current) {
      observer.observe(projectsRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const openModal = (project: Project) => {
    setSelectedProject(project);
    trackProjectView(project.title);
    document.body.style.overflow = 'hidden'; // Prevent scrolling when modal is open
  };

  const closeModal = () => {
    setSelectedProject(null);
    document.body.style.overflow = 'auto'; // Re-enable scrolling
  };

  return (
    <section id="projects" className="projects">
      <div className="section-container">
        <h2 className="section-title">My Projects</h2>
        <p className="section-subtitle">Here are some of my recent projects. Click on any project to learn more.</p>
        
        <div className="projects-grid" ref={projectsRef}>
          {projects.map((project, index) => (
            <div 
              key={project.id} 
              className={`project-card ${isVisible ? 'animate-in' : ''}`}
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => openModal(project)}
            >
              <div className="project-image">
                <img src={project.imageUrl} alt={project.title} />
              </div>
              <div className="project-info">
                <h3>{project.title}</h3>
                <p>{project.description}</p>
                <div className="project-tech">
                  {project.technologies.slice(0, 3).map((tech, index) => (
                    <span key={index} className="tech-tag">
                      {tech}{index < project.technologies.slice(0, 3).length - 1 ? ', ' : ''}
                    </span>
                  ))}
                  {project.technologies.length > 3 && (
                    <span className="tech-tag">+{project.technologies.length - 3}</span>
                  )}
                </div>
              </div>
              <div className="project-overlay">
                <span>View Details</span>
              </div>
            </div>
          ))}
        </div>

        {/* Project Modal */}
        {selectedProject && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>&times;</button>
              <div className="modal-header">
                <h2>{selectedProject.title}</h2>
              </div>
              <div className="modal-body">
                <div className="modal-description">
                  <div dangerouslySetInnerHTML={{ __html: selectedProject.fullDescription }}></div>
                  <div className="modal-tech">
                    <h4>Technologies Used:</h4>
                    <div className="tech-tags">
                      {selectedProject.technologies.map((tech, index) => (
                        <span key={index} className="tech-tag">
                          {tech}{index < selectedProject.technologies.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                {selectedProject.projectUrl && (
                  <a 
                    href={selectedProject.projectUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="modal-button primary-button"
                  >
                    Visit Project
                  </a>
                )}
                {selectedProject.githubUrl && selectedProject.githubUrl !== '#' && (
                  <a 
                    href={selectedProject.githubUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="modal-button secondary-button"
                  >
                    View Code
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Projects;